import type { KeyboardEvent, ReactNode, RefObject } from 'react';
import CountUp from './CountUp';
import { useSpotlight } from '@/components/motion/motionHelpers';

type StatTone = 'primary' | 'accent' | 'secondary' | 'neutral';

const iconTones: Record<StatTone, string> = {
  primary: 'bg-primary-100 text-primary-700',
  accent: 'bg-accent-100 text-accent-700',
  secondary: 'bg-secondary-100 text-secondary-800',
  neutral: 'bg-background-200 text-foreground-700',
};

interface StatCardProps {
  label: string;
  value: ReactNode;
  icon: string;
  tone?: StatTone;
  delta?: string;
  deltaPositive?: boolean;
  hint?: string;
  /** Makes the whole card actionable. */
  onClick?: () => void;
  /** Makes the delta badge its own actionable target (independent of onClick). */
  onDeltaClick?: () => void;
  /** Highlights the card as the currently applied filter. */
  active?: boolean;
}

export default function StatCard({
  label,
  value,
  icon,
  tone = 'neutral',
  delta,
  deltaPositive = true,
  hint,
  onClick,
  onDeltaClick,
  active = false,
}: StatCardProps) {
  const cardInteractive = Boolean(onClick);
  const glow = useSpotlight();

  const handleCardKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const handleDeltaKey = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (!onDeltaClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      onDeltaClick();
    }
  };

  const cardClass = [
    'stat-glow w-full rounded-lg border bg-background-50 p-4 text-left transition duration-200',
    active
      ? 'border-primary-400 ring-1 ring-primary-300/70'
      : 'border-background-200 hover:border-background-300',
    cardInteractive
      ? `cursor-pointer hover:-translate-y-0.5 hover:shadow-md motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 ${
          active ? '' : 'hover:border-primary-300'
        }`
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const deltaClass = `mb-0.5 inline-flex items-center gap-0.5 text-xs font-medium ${
    deltaPositive ? 'text-accent-700' : 'text-primary-700'
  }`;

  return (
    <div
      ref={glow.ref as RefObject<HTMLDivElement>}
      onPointerMove={glow.onPointerMove}
      onPointerLeave={glow.onPointerLeave}
      className={`group ${cardClass}`}
      onClick={onClick}
      role={cardInteractive ? 'button' : undefined}
      tabIndex={cardInteractive ? 0 : undefined}
      onKeyDown={cardInteractive ? handleCardKey : undefined}
    >
      <div className="flex items-start justify-between">
        <span className="flex items-center gap-1 text-xs font-label font-medium text-foreground-500">
          {label}
          {cardInteractive ? (
            <i
              className={`ri-arrow-right-up-line text-[12px] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${
                active ? 'text-primary-600' : 'text-foreground-400'
              }`}
              aria-hidden="true"
            />
          ) : null}
        </span>
        <span
          className={`stat-icon w-8 h-8 flex items-center justify-center rounded-md text-base ${iconTones[tone]}`}
        >
          <i className={icon} />
        </span>
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span className="font-heading text-2xl font-semibold text-foreground-950">
          {typeof value === 'string' ? <CountUp value={value} /> : value}
        </span>
        {delta ? (
          onDeltaClick ? (
            <span
              className={`${deltaClass} cursor-pointer underline decoration-dotted underline-offset-2 rounded-sm transition-colors hover:text-foreground-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400`}
              role="button"
              tabIndex={0}
              onClick={(event) => {
                event.stopPropagation();
                onDeltaClick();
              }}
              onKeyDown={handleDeltaKey}
            >
              <i
                className={
                  deltaPositive
                    ? 'ri-arrow-up-line text-[12px]'
                    : 'ri-arrow-down-line text-[12px]'
                }
              />
              {delta}
            </span>
          ) : (
            <span className={deltaClass}>
              <i
                className={
                  deltaPositive
                    ? 'ri-arrow-up-line text-[12px]'
                    : 'ri-arrow-down-line text-[12px]'
                }
              />
              {delta}
            </span>
          )
        ) : null}
      </div>
      {hint ? <p className="mt-1 text-xs text-foreground-500">{hint}</p> : null}
    </div>
  );
}